import React, { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Copy, Plus, Trash2, Key, Loader2, ExternalLink, Server, Wifi } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const MCPSettings = () => {
  const { currentWorkspace } = useWorkspace();
  const [apiKeys, setApiKeys] = useState([]);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const wsId = currentWorkspace?.workspace_id;

  const fetchData = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const [keysRes, infoRes] = await Promise.all([
        apiFetch(`${API}/api/workspaces/${wsId}/mcp/api-keys`),
        apiFetch(`${API}/api/mcp/connection-info`)
      ]);
      const keysData = await keysRes.json();
      const infoData = await infoRes.json();
      setApiKeys(keysData.keys || []);
      setConnectionInfo(infoData);
    } catch (e) {
      console.error('Failed to load MCP data:', e);
    }
    setLoading(false);
  }, [wsId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createKey = async () => {
    setCreating(true);
    try {
      const res = await apiFetch(`${API}/api/workspaces/${wsId}/mcp/api-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newKeyName || 'MCP API Key' })
      });
      const data = await res.json();
      setCreatedKey(data);
      setNewKeyName('');
      fetchData();
    } catch (e) {
      console.error('Failed to create key:', e);
    }
    setCreating(false);
  };

  const revokeKey = async (keyId) => {
    try {
      await apiFetch(`${API}/api/workspaces/${wsId}/mcp/api-keys/${keyId}`, {
        method: 'DELETE'
      });
      fetchData();
    } catch (e) {
      console.error('Failed to revoke key:', e);
    }
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mcp-settings">
      {/* Connection Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Server className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <CardTitle className="text-lg">MCP Server</CardTitle>
              <CardDescription>Connect AI clients to your financial data via Model Context Protocol</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {connectionInfo && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                  <p className="text-xs text-zinc-400 mb-1">SSE Endpoint</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-emerald-400 flex-1 truncate" data-testid="mcp-sse-url">
                      {connectionInfo.sse_url}
                    </code>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => copyToClipboard(connectionInfo.sse_url, 'sse')}
                      data-testid="copy-sse-url"
                    >
                      <Copy className={`w-3 h-3 ${copiedId === 'sse' ? 'text-emerald-400' : ''}`} />
                    </Button>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                  <p className="text-xs text-zinc-400 mb-1">Transport</p>
                  <div className="flex items-center gap-2">
                    <Wifi className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-medium">{connectionInfo.transport?.toUpperCase()}</span>
                    <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 text-xs">Active</Badge>
                  </div>
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <p className="text-xs text-zinc-400 mb-2">Available Tools ({connectionInfo.available_tools?.length || 0})</p>
                <div className="flex flex-wrap gap-1.5">
                  {connectionInfo.available_tools?.map((tool, i) => (
                    <Badge key={i} variant="secondary" className="text-xs bg-zinc-700 text-zinc-300">
                      {tool.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* API Keys */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Key className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <CardTitle className="text-lg">API Keys</CardTitle>
                <CardDescription>Manage access keys for MCP clients</CardDescription>
              </div>
            </div>
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="create-api-key-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Key
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {apiKeys.length === 0 ? (
            <div className="text-center py-8 text-zinc-400">
              <Key className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No API keys yet. Create one to connect AI clients.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.key_id} data-testid={`api-key-row-${key.key_id}`}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell>
                      <code className="text-xs text-zinc-400">{key.api_key}</code>
                    </TableCell>
                    <TableCell className="text-xs text-zinc-400">
                      {key.created_at ? new Date(key.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-xs text-zinc-400">
                      {key.last_used ? new Date(key.last_used).toLocaleDateString() : 'Never'}
                    </TableCell>
                    <TableCell className="text-xs">{key.usage_count || 0}</TableCell>
                    <TableCell>
                      <Badge variant={key.active ? "default" : "destructive"} className={key.active ? "bg-emerald-500/20 text-emerald-400" : ""}>
                        {key.active ? 'Active' : 'Revoked'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {key.active && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => revokeKey(key.key_id)}
                          className="text-red-400 hover:text-red-300"
                          data-testid={`revoke-key-${key.key_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Client Config Example */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <ExternalLink className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <CardTitle className="text-lg">Client Configuration</CardTitle>
              <CardDescription>Example configuration for Claude Desktop and other MCP clients</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <pre className="p-4 rounded-lg bg-zinc-900 border border-zinc-700 text-xs overflow-x-auto">
            <code className="text-zinc-300">{JSON.stringify({
              "mcpServers": {
                "countbucks": {
                  "transport": {
                    "type": "sse",
                    "url": connectionInfo?.sse_url || "<your-server-url>/api/mcp/sse"
                  }
                }
              }
            }, null, 2)}</code>
          </pre>
          <p className="text-xs text-zinc-500 mt-2">
            Add this to your Claude Desktop configuration file or MCP client settings.
            Pass <code className="text-emerald-400">workspace_id</code> as a parameter to each tool call.
          </p>
        </CardContent>
      </Card>

      {/* Create Key Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create MCP API Key</DialogTitle>
            <DialogDescription>Generate a new key for AI client access to your financial data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              placeholder="Key name (e.g., Claude Desktop)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              data-testid="new-key-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={createKey}
              disabled={creating}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="confirm-create-key"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Created Key Display Dialog */}
      <Dialog open={!!createdKey} onOpenChange={() => setCreatedKey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API Key Created</DialogTitle>
            <DialogDescription>Copy this key now. It won't be shown again in full.</DialogDescription>
          </DialogHeader>
          <div className="p-4 rounded-lg bg-zinc-900 border border-emerald-500/30">
            <code className="text-sm text-emerald-400 break-all" data-testid="new-api-key-value">
              {createdKey?.api_key}
            </code>
          </div>
          <DialogFooter>
            <Button
              onClick={() => copyToClipboard(createdKey?.api_key, 'new-key')}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="copy-new-key"
            >
              <Copy className="w-4 h-4 mr-2" />
              {copiedId === 'new-key' ? 'Copied!' : 'Copy Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
